import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAIAgents, AIAgent } from '../hooks/useAIAgents';
import { api } from '../utils/api';
import { AssistantConfig } from '../types/assistant.types';

interface AIAgentContextValue {
  agents: AIAgent[];
  loading: boolean;
  error: string | null;
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string | null) => void;
  listAgents: () => Promise<AIAgent[]>;
  sessionId: string;
  setSessionId: (id: string) => void;
  globalPrompt: string;
  setGlobalPrompt: (prompt: string) => void;
  voiceEnabled: boolean;
  setVoiceEnabled: (enabled: boolean) => void;
  saveSettings: (assistantId: string, updates: Partial<AssistantConfig>) => Promise<void>;
}

const AIAgentContext = createContext<AIAgentContextValue | undefined>(undefined);

interface ProviderProps {
  children: React.ReactNode;
}

export const AIAgentProvider: React.FC<ProviderProps> = ({ children }) => {
  const { loading, error, agents, listAgents } = useAIAgents();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => `session-${Date.now()}`);
  const [globalPrompt, setGlobalPrompt] = useState<string>('');
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(false);

  // Ensure we have agents loaded at least once globally
  useEffect(() => {
    listAgents().then((list) => {
      if (!selectedAgentId && Array.isArray(list) && list.length > 0) {
        setSelectedAgentId(list[0].id);
      }
    }).catch(() => {});
  }, [listAgents]);

  const saveSettings = useCallback(async (assistantId: string, updates: Partial<AssistantConfig>) => {
    if (!assistantId) return;
    try {
      await api.put(`/assistants/${assistantId}`, updates);
    } catch (e) {
      // no-op; callers can handle errors
    }
  }, []);

  const value: AIAgentContextValue = useMemo(() => ({
    agents,
    loading,
    error,
    selectedAgentId,
    setSelectedAgentId,
    listAgents,
    sessionId,
    setSessionId,
    globalPrompt,
    setGlobalPrompt,
    voiceEnabled,
    setVoiceEnabled,
    saveSettings,
  }), [agents, loading, error, selectedAgentId, listAgents, sessionId, globalPrompt, voiceEnabled, saveSettings]);

  return (
    <AIAgentContext.Provider value={value}>
      {children}
    </AIAgentContext.Provider>
  );
};

export const useAIAgentContext = (): AIAgentContextValue => {
  const ctx = useContext(AIAgentContext);
  if (!ctx) {
    throw new Error('useAIAgentContext must be used within AIAgentProvider');
  }
  return ctx;
};


